<template>
  <tabs />
  <toolbar class="toolbar">
    <filter-pane v-model:page="page" v-model="filter" :filters="filters" />
    <add-fields-pane v-model="visibleColumns" :fields="columns" />
    <action
      :icon="AddIcon"
      :label="$t('pages.faqList.modal.new.buttonText')"
      @click="faqModalOpen = true"
    />
    <export
      url="/faq_questions/csv"
      :filter="filter"
      :columns="columns"
      :visible-columns="visibleColumns"
      :title="$t('csv.title.faqQuestions')"
      :sort-key="sortKey"
      :sort-direction="sortDirection"
    />
  </toolbar>
  <faq-question-modal v-model:open="faqModalOpen" v-model:faq="editedFaq" @saved="refetch" />
  <data-table
    v-model:sort-by="sortKey"
    v-model:sort-direction="sortDirection"
    class="mb-auto ml-12"
    :busy="loading"
    :items="records"
    :columns="columns"
    :visible-columns="visibleColumns"
    :actions="actions"
  />
  <pagination v-model:page="page" v-model:per-page="perPage" class="pagination" :total="total" />
</template>

<script lang="ts" setup>
import { ref, reactive, computed, unref } from 'vue';
import { useI18n } from 'vue-i18n';
import FilterPane from '@utils/toolbar/panes/Filter.vue';
import AddFieldsPane from '@utils/toolbar/panes/AddFields.vue';
import Toolbar from '@utils/toolbar/Toolbar.vue';
import Pagination from '@utils/table/Pagination.vue';
import DataTable, { type Column } from '@utils/table/Table.vue';
import { collOptionsProvider, useProvider } from '@utils/table/hooks/provider';
import Action from '@utils/toolbar/Action.vue';
import AddIcon from '@images/panes/add.svg';

import type { Filter } from '@utils/filter/Filter.vue';
import { useDelete } from '@hooks/api';
import Tabs from '@components/structure/navigation/Tabs.vue';
import FaqQuestionModal from '@pages/content/faq/FaqQuestionModal.vue';
import type { HydraCollection, FaqQuestion, FaqSection } from '@/entities/Entities';
import { translationFor } from '@/utils/api';
import Export from '@utils/toolbar/panes/Export.vue';
import { useQueryRef } from '@/hooks/url';
import {useTableFields} from '@hooks/table-configuration';

const page = useQueryRef('page', 1);
const perPage = useQueryRef('perPage', 10);
const filter = useQueryRef('filter', {});

const faqModalOpen = ref(false);
const editedFaq = ref();

const sortKey = useQueryRef<string | undefined>('sortKey', undefined);
const sortDirection = useQueryRef<'ASC' | 'DESC' | undefined>('sortDirection', undefined);

const { t } = useI18n();

const [deleteFaq, { loading: deleting }] = useDelete();

const providerParams = reactive({
  sort: {
    sortKey,
    sortDirection,
  },
  filter,
  pagination: {
    page,
    perPage,
  },
});

const actions = [
  {
    title: t('pages.faqList.actions.edit'),
    action: (item: FaqQuestion) => {
      faqModalOpen.value = true;
      editedFaq.value = { ...item };
    },
  },
  {
    title: t('pages.faqList.actions.delete'),
    action: async (item: FaqQuestion) => {
      if (await deleteFaq(`/faq_questions/${item.id}`)) {
        // eslint-disable-next-line no-use-before-define
        refetch();
      }
    },
  },
];

const translate = translationFor();

type ColumnFilter = Partial<Filter> & Omit<Filter, 'key'>;
type ColumnDefinition = Column & { filter?: ColumnFilter | ColumnFilter[] } & { group: string };

const columns: ColumnDefinition[] = [
  {
    key: 'id',
    title: t('id'),
    group: t('pages.faqList.fieldGroups.general'),
    class: 'whitespace-nowrap',
  },
  {
    key: 'orderNumber',
    title: t('faqQuestion.orderNumber'),
    group: t('pages.faqList.filterGroups.category'),
    visible: true,
  },
  {
    key: 'section.translations.title.en',
    title: t('faqQuestion.section'),
    group: t('pages.faqList.fieldGroups.general'),
    computed: (item: FaqQuestion) => translate(item.section, 'title') ?? '',
    visible: true,
    filter: {
      key: 'section',
      type: 'dropdown',
      group: t('pages.faqList.filterGroups.general'),
      options: {
        options: collOptionsProvider<FaqSection>('/faq_sections', (section) => ({
          label: (translate(section, 'title') as string) ?? '',
          value: section.id,
        })),
        label: t('faqQuestion.section'),
      },
    },
  },
  {
    key: 'translations.question.en',
    isHtml: true,
    title: t('faqQuestion.question'),
    group: t('pages.faqList.fieldGroups.general'),
    computed: (item: FaqQuestion) => translate(item, 'question') ?? '',
    visible: true,
    filter: {
      type: 'text',
      group: t('pages.faqList.filterGroups.general'),
      options: {
        label: t('faqQuestion.question'),
      },
    },
  },
  {
    key: 'translations.answer.en',
    isHtml: true,
    title: t('faqQuestion.answer'),
    group: t('pages.faqList.fieldGroups.general'),
    computed: (item: FaqQuestion) => translate(item, 'answer') ?? '',
    visible: true,
    filter: {
      type: 'text',
      group: t('pages.faqList.filterGroups.general'),
      options: {
        label: t('faqQuestion.answer'),
      },
    },
  },
];

const visibleColumns = useTableFields(
    columns,
);

const {
  data: items,
  loading: loadingTable,
  refetch,
} = useProvider<HydraCollection<FaqQuestion>>('/faq_questions', providerParams);

const records = computed(() => unref(items)?.['hydra:member']);
const total = computed(() => unref(items)?.['hydra:totalItems']);
const filters = computed(() =>
  columns.flatMap((col): Filter[] => {
    if (!col.filter) return [];
    if (Array.isArray(col.filter)) {
      return col.filter.map((f) => ({
        key: col.key,
        ...f,
      }));
    }
    return [{ key: col.key, ...col.filter }];
  })
);

const loading = computed(() => loadingTable.value || deleting.value);
</script>

<style lang="scss" scoped>
button {
  @apply block;
}

.table {
  @apply flex flex-col;
}
.medium {
  width: 300px;
  height: auto;
}
</style>

